import React, { useEffect, useMemo, useState } from 'react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import {
  Activity, Mail, MousePointerClick, Eye, Send, HelpCircle,
  Copy, ExternalLink, ClipboardList, TestTube2, FlaskConical, Share2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';

/**
 * Advisor Analytics section — renders inside the Overview tab. When the dealer
 * has no data yet, every empty chart is replaced with a focused empty state
 * that tells them *why* it's empty and gives them one thing they can do to
 * fix it. Once real data lands, the charts render normally.
 */

const COLORS = {
  travel_trailer: '#22c55e',
  fifth_wheel: '#f59e0b',
  motorhome: '#3b82f6',
  class_a: '#8b5cf6',
  class_b: '#06b6d4',
  class_c: '#ec4899',
  toy_hauler: '#f43f5e',
  pop_up: '#10b981',
  unknown: '#94a3b8',
};

const RANGE_LABELS = {
  7: 'Last 7 days',
  30: 'Last 30 days',
  90: 'Last 90 days',
};

// Reusable stat card with tooltip
const StatCard = ({ title, value, subtitle, tooltip, icon: Icon, accent = 'from-[#004E86] to-[#0ea5e9]', isDark }) => (
  <div
    className={`relative overflow-hidden rounded-xl border p-4 ${
      isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
    }`}
    title={tooltip}
  >
    <div className={`absolute inset-0 opacity-10 bg-gradient-to-br ${accent}`} />
    <div className="relative flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <p className={`text-xs uppercase tracking-wide ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>{title}</p>
          {tooltip && (
            <span className={isDark ? 'text-gray-500' : 'text-gray-400'} title={tooltip}>
              <HelpCircle size={11} />
            </span>
          )}
        </div>
        <p className={`mt-1 text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{value}</p>
        {subtitle && (
          <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{subtitle}</p>
        )}
      </div>
      <div className={`rounded-lg p-2 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
        <Icon className={isDark ? 'text-gray-100' : 'text-gray-700'} size={18} />
      </div>
    </div>
  </div>
);

// Reusable "empty state" card that replaces an empty chart. Explains WHY it's
// empty and gives the dealer one focused action to unblock the funnel.
const EmptyStateCard = ({ isDark, icon: Icon, title, body, buttons = [] }) => (
  <div
    className={`h-full flex flex-col items-center justify-center text-center rounded-xl border p-6 ${
      isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
    }`}
  >
    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-3 ${
      isDark ? 'bg-white/5 text-slate-300' : 'bg-gray-100 text-gray-500'
    }`}>
      <Icon size={22} />
    </div>
    <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</p>
    <p className={`text-xs mt-1.5 max-w-xs ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{body}</p>
    {buttons.length > 0 && (
      <div className="mt-4 flex flex-wrap gap-2 justify-center">
        {buttons.map((b, i) => (
          <button
            key={i}
            type="button"
            onClick={b.onClick}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md transition-colors ${
              b.variant === 'ghost'
                ? isDark ? 'border border-white/15 text-white hover:bg-white/10' : 'border border-gray-300 text-gray-700 hover:bg-gray-100'
                : isDark ? 'bg-amber-400 text-black hover:bg-amber-300' : 'bg-amber-500 text-white hover:bg-amber-600'
            }`}
            data-testid={b.testId}
          >
            {b.icon ? <b.icon size={12} /> : null}
            {b.text}
          </button>
        ))}
      </div>
    )}
  </div>
);

const DealerAnalyticsSection = ({
  analytics,
  isLoading,
  isDark,
  dealer,
  onRangeChange,
  onGoTo,
  onCreateTestLead,
  onCopyLink,
}) => {
  const [rangeDays, setRangeDays] = useState(30);
  const [includeTest, setIncludeTest] = useState(false);
  const [chartsReady, setChartsReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setChartsReady(true), 50);
    return () => clearTimeout(t);
  }, []);

  const rvPieData = useMemo(() => {
    const breakdown = analytics?.rv_type_breakdown || {};
    const rows = Object.entries(breakdown)
      .map(([key, value]) => ({
        key,
        name: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        value: value || 0,
      }))
      .filter((r) => r.value > 0);
    return rows;
  }, [analytics]);

  const bars = useMemo(() => (
    [
      { name: 'Sent', value: analytics?.emails_sent || 0 },
      { name: 'Unique Opens', value: analytics?.emails_opened || 0 },
      { name: 'Total Opens', value: analytics?.email_views || 0 },
      { name: 'Match Clicks', value: analytics?.results_clicks || 0 },
    ]
  ), [analytics]);

  const hasEmailActivity = bars.some((b) => b.value > 0);
  const hasRvPreferenceData = rvPieData.length > 0;

  const handleRange = (d) => {
    setRangeDays(d);
    onRangeChange?.(d, includeTest);
  };
  const handleTestToggle = () => {
    const next = !includeTest;
    setIncludeTest(next);
    onRangeChange?.(rangeDays, next);
  };

  const copyLink = async () => {
    const slug = (dealer?.slug || '').trim();
    const url = slug ? `${window.location.origin}/${slug}` : `${window.location.origin}/quiz`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Copied ${url}`);
      onCopyLink?.();
    } catch {
      toast.error('Copy failed — grab it from Marketing → Sharing.');
    }
  };
  const openPublicPage = () => {
    const slug = (dealer?.slug || '').trim();
    if (!slug) {
      onGoTo?.('marketing');
      return;
    }
    window.open(`${window.location.origin}/${slug}`, '_blank', 'noopener');
  };

  return (
    <div className="mt-6" data-testid="dealer-analytics" suppressHydrationWarning>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2
            className={`text-lg font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Advisor Analytics
          </h2>
          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Technical snapshot of your funnel — email engagement, link clicks, and lead RV preferences.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {[7, 30, 90].map((d) => (
            <Button
              key={d}
              size="sm"
              variant="outline"
              onClick={() => handleRange(d)}
              className={`${
                rangeDays === d
                  ? isDark
                    ? 'bg-amber-400 text-black border-amber-400 hover:bg-amber-300'
                    : 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600'
                  : isDark
                    ? 'border-gray-700 text-gray-200'
                    : ''
              }`}
              data-testid={`analytics-range-${d}`}
              aria-pressed={rangeDays === d}
            >
              {d}d
            </Button>
          ))}
          <span className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
            {RANGE_LABELS[rangeDays] || `Last ${rangeDays} days`}
          </span>

          {/* Include-test toggle — off by default so real perf metrics stay honest */}
          <label
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border cursor-pointer transition-colors ${
              includeTest
                ? isDark ? 'bg-fuchsia-500/15 border-fuchsia-500/40 text-fuchsia-200' : 'bg-fuchsia-50 border-fuchsia-300 text-fuchsia-700'
                : isDark ? 'border-gray-700 text-slate-400 hover:bg-white/5' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
            data-testid="analytics-include-test-toggle"
            title="Include synthetic test leads in these metrics. Off by default so real performance stays clean."
          >
            <input type="checkbox" className="hidden" checked={includeTest} onChange={handleTestToggle} />
            <FlaskConical size={12} />
            {includeTest ? 'Test data included' : 'Include test data'}
          </label>
        </div>
      </div>

      {/* Renamed 5-tile row with tooltips */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-4">
        <StatCard
          title="Emails Sent"
          value={isLoading ? '…' : (analytics?.emails_sent ?? 0)}
          subtitle="Outreach to your leads"
          tooltip="Total number of emails sent from your Email Center to your leads."
          icon={Send}
          accent="from-emerald-500 to-cyan-400"
          isDark={isDark}
        />
        <StatCard
          title="Unique Opens"
          value={isLoading ? '…' : (analytics?.emails_opened ?? 0)}
          subtitle="Individual recipients"
          tooltip="Distinct emails where at least one open was tracked. Deduplicated per email."
          icon={Eye}
          accent="from-fuchsia-500 to-pink-400"
          isDark={isDark}
        />
        <StatCard
          title="Total Opens"
          value={isLoading ? '…' : (analytics?.email_views ?? 0)}
          subtitle="Every open recorded"
          tooltip="Every open event captured, including repeat opens by the same recipient."
          icon={Activity}
          accent="from-blue-500 to-indigo-400"
          isDark={isDark}
        />
        <StatCard
          title="Match Result Clicks"
          value={isLoading ? '…' : (analytics?.results_clicks ?? 0)}
          subtitle="Clicks into My Matches"
          tooltip="How many times a lead clicked through from a sent email into their /my-matches page."
          icon={MousePointerClick}
          accent="from-amber-500 to-yellow-300"
          isDark={isDark}
        />
        <StatCard
          title="Advisor Application-Link Clicks"
          value={isLoading ? '…' : (analytics?.dealer_link_clicks ?? 0)}
          subtitle="Visits to your public link"
          tooltip="Visits to your /slug landing page. Not deduplicated by visitor."
          icon={MousePointerClick}
          accent="from-slate-500 to-slate-300"
          isDark={isDark}
        />
      </div>

      {/* Chart row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {/* Email Engagement */}
        <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h3 className={`text-sm font-bold uppercase tracking-wide ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
            Email Engagement
          </h3>
          <div className="mt-3 w-full" style={{ minWidth: 0, minHeight: 260 }}>
            {!hasEmailActivity ? (
              <EmptyStateCard
                isDark={isDark}
                icon={Mail}
                title="No email engagement yet."
                body="Email activity will appear after a shopper completes your RV Match quiz."
                buttons={[
                  { text: 'Share My Quiz', icon: Share2, onClick: copyLink, testId: 'empty-email-share' },
                ]}
              />
            ) : (
              chartsReady && (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={bars} margin={{ top: 10, right: 20, left: -10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
                    <XAxis dataKey="name" stroke={isDark ? '#cbd5e1' : '#6b7280'} fontSize={12} />
                    <YAxis stroke={isDark ? '#cbd5e1' : '#6b7280'} fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        background: isDark ? '#111827' : 'white',
                        border: isDark ? '1px solid #374151' : '1px solid #e5e7eb',
                        color: isDark ? 'white' : '#111827',
                      }}
                    />
                    <Bar dataKey="value" fill={isDark ? '#60a5fa' : '#004E86'} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )
            )}
          </div>
        </div>

        {/* RV Type Preferences */}
        <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h3 className={`text-sm font-bold uppercase tracking-wide ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
            Lead RV Type Preferences
          </h3>
          <div className="mt-3 w-full" style={{ minWidth: 0, minHeight: 260 }}>
            {!hasRvPreferenceData ? (
              <EmptyStateCard
                isDark={isDark}
                icon={ClipboardList}
                title="No shopper preferences yet."
                body="RV-type trends will appear as shoppers complete their matches."
                buttons={[
                  { text: 'Create Test Lead', icon: TestTube2, onClick: onCreateTestLead, testId: 'empty-rv-test-lead' },
                  { text: 'View Public Page', icon: ExternalLink, onClick: openPublicPage, variant: 'ghost', testId: 'empty-rv-public-page' },
                ]}
              />
            ) : (
              chartsReady && (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={rvPieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                      {rvPieData.map((entry) => (
                        <Cell key={entry.key} fill={COLORS[entry.key] || COLORS.unknown} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: isDark ? '#111827' : 'white',
                        border: isDark ? '1px solid #374151' : '1px solid #e5e7eb',
                        color: isDark ? 'white' : '#111827',
                      }}
                    />
                    <Legend wrapperStyle={{ color: isDark ? '#e5e7eb' : '#111827' }} />
                  </PieChart>
                </ResponsiveContainer>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealerAnalyticsSection;
